import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  ArrowLeftIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  TrashIcon,
  ArrowPathIcon,
  DocumentIcon,
  MapPinIcon,
  EyeSlashIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { getAdminChatDetails, deleteAdminMessage, deleteAdminChat } from '../api/adminChats';
import { Avatar, ConfirmDialog } from './ChatList';

const formatTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '';

const formatDay = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Сегодня';
  if (d.toDateString() === yesterday.toDateString()) return 'Вчера';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
};

const Copy = ({ value }) => {
  const [done, setDone] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(String(value));
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        } catch {
          /* clipboard недоступен */
        }
      }}
      aria-label="Скопировать"
      className="rounded p-0.5 text-text-muted hover:text-text-primary"
    >
      {done ? <CheckIcon className="h-3.5 w-3.5 text-emerald-500" /> : <ClipboardDocumentIcon className="h-3.5 w-3.5" />}
    </button>
  );
};

const Participant = ({ user, side }) => (
  <div className="flex items-start gap-3">
    <Avatar user={user} className="h-10 w-10 text-sm" />
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <span className="truncate text-sm font-semibold text-text-primary">{user?.name || 'Неизвестно'}</span>
        <span className="shrink-0 rounded bg-bg-tertiary px-1.5 py-px text-[10px] font-medium text-text-muted">{side}</span>
      </div>
      {user?.id && <div className="font-mono text-xs text-text-muted">ID {user.id}</div>}
      {user?.phone && (
        <div className="mt-1 flex items-center gap-1.5 text-xs text-text-secondary">
          <PhoneIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{user.phone}</span>
          <Copy value={user.phone} />
        </div>
      )}
      {user?.email && (
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-text-secondary">
          <EnvelopeIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{user.email}</span>
          <Copy value={user.email} />
        </div>
      )}
    </div>
  </div>
);

const MessageBody = ({ message }) => {
  const { message_type: type, file_info: file } = message;
  if (type === 'location' && message.latitude && message.longitude) {
    return (
      <a
        href={`https://maps.google.com/?q=${message.latitude},${message.longitude}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 underline-offset-2 hover:underline"
      >
        <MapPinIcon className="h-4 w-4 shrink-0" />
        {message.location_name || 'Геолокация'}
      </a>
    );
  }
  if (file && (type === 'image' || /\.(jpe?g|png|webp|gif)$/i.test(file.url))) {
    // Жазусыз суретте message = file_url (ескі клиенттер үшін) — оны жазу ретінде көрсетпейміз
    const caption = message.message && message.message !== file.url ? message.message : '';
    return (
      <div>
        <a href={file.url} target="_blank" rel="noopener noreferrer" className="-mx-3.5 -mt-2 block">
          <img src={file.url} alt={file.name} loading="lazy" className="block max-h-72 w-full object-cover" />
        </a>
        {caption && <p className="mt-2 whitespace-pre-wrap break-words">{caption}</p>}
      </div>
    );
  }
  if (file && type === 'audio') {
    return <audio controls src={file.url} className="h-9 max-w-full" />;
  }
  if (file) {
    return (
      <a href={file.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 underline-offset-2 hover:underline">
        <DocumentIcon className="h-4 w-4 shrink-0" />
        <span className="truncate">{file.name}</span>
      </a>
    );
  }
  return <p className="whitespace-pre-wrap break-words">{message.message || <i className="opacity-60">Пустое сообщение</i>}</p>;
};

const ChatDetails = ({ chat, onBack, onRefresh }) => {
  const [chatDetails, setChatDetails] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null); // { kind: 'chat' } | { kind: 'message', message }
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const scrollRef = useRef(null);

  const fetchChatDetails = useCallback(async () => {
    if (!chat?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminChatDetails(chat.id);
      setChatDetails(data.chat);
      setMessages(data.messages || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching chat details:', err);
    } finally {
      setLoading(false);
    }
  }, [chat?.id]);

  useEffect(() => {
    fetchChatDetails();
  }, [fetchChatDetails]);

  // Открываем переписку на последних сообщениях
  useEffect(() => {
    if (!loading && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [loading]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && !confirm && onBack();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirm, onBack]);

  // В детальном ответе нет телефонов — берём их из строки списка
  const user1 = useMemo(() => ({ ...chat?.user1, ...chatDetails?.user1, phone: chat?.user1?.phone }), [chat, chatDetails]);
  const user2 = useMemo(() => ({ ...chat?.user2, ...chatDetails?.user2, phone: chat?.user2?.phone }), [chat, chatDetails]);
  const hidden = chatDetails?.status === 'deleted' || Boolean(chatDetails?.deleted_at);

  const runConfirmed = async () => {
    setBusy(true);
    setActionError(null);
    try {
      if (confirm.kind === 'chat') {
        await deleteAdminChat(chat.id);
        onRefresh();
        onBack();
        return;
      }
      await deleteAdminMessage(chat.id, confirm.message.id);
      setMessages((prev) =>
        prev.map((m) => (m.id === confirm.message.id ? { ...m, deleted_at: new Date().toISOString() } : m))
      );
      setConfirm(null);
      onRefresh();
    } catch (err) {
      console.error('Chat action failed:', err);
      setActionError(/403/.test(err.message) ? 'Системные чаты нельзя скрыть.' : err.message);
    } finally {
      setBusy(false);
    }
  };

  const visibleCount = messages.filter((m) => !m.deleted_at).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          title="К списку (Esc)"
          aria-label="Назад к списку"
          className="rounded-lg border border-border-primary p-2 text-text-secondary hover:bg-bg-tertiary hover:text-text-primary"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-lg font-semibold text-text-primary">
            {user1.name || '…'} <span className="font-normal text-text-muted">↔</span> {user2.name || '…'}
          </h3>
          <p className="text-xs text-text-secondary">
            Чат <span className="font-mono">#{chat.id}</span>
            {chatDetails?.created_at && ` · создан ${new Date(chatDetails.created_at).toLocaleDateString('ru-RU')}`}
            {!loading && ` · ${visibleCount} сообщ.`}
          </p>
        </div>
        {hidden && (
          <span className="inline-flex items-center gap-1 rounded-md border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-xs font-semibold text-rose-500">
            <EyeSlashIcon className="h-4 w-4" />
            Скрыт
          </span>
        )}
        <button
          type="button"
          onClick={fetchChatDetails}
          title="Обновить"
          aria-label="Обновить"
          className="rounded-lg border border-border-primary p-2 text-text-secondary hover:bg-bg-tertiary"
        >
          <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error ? (
        <div className="flex flex-col items-center gap-3 rounded-block border border-border-primary bg-bg-card px-6 py-14 text-center">
          <ExclamationTriangleIcon className="h-10 w-10 text-rose-500/80" />
          <p className="text-sm text-text-secondary">Не удалось загрузить чат</p>
          <button
            type="button"
            onClick={fetchChatDetails}
            className="inline-flex items-center gap-2 rounded-lg border border-border-primary px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-bg-tertiary"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Повторить
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Sidebar */}
          <aside className="space-y-4 lg:order-2">
            <div className="space-y-4 rounded-block border border-border-primary bg-bg-card p-4 shadow-sm">
              <Participant user={user1} side="слева" />
              <div className="border-t border-border-primary" />
              <Participant user={user2} side="справа" />
            </div>

            {chatDetails?.announcement_link && (
              <a
                href={chatDetails.announcement_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-2 rounded-block border border-border-primary bg-bg-card p-4 text-sm font-medium text-text-primary shadow-sm hover:bg-bg-tertiary"
              >
                <span>
                  Объявление
                  {chatDetails.announcement_id && <span className="ml-1 font-mono text-text-muted">#{chatDetails.announcement_id}</span>}
                </span>
                <ArrowTopRightOnSquareIcon className="h-4 w-4 text-text-muted" />
              </a>
            )}

            {!hidden && chatDetails && (
              <button
                type="button"
                onClick={() => {
                  setActionError(null);
                  setConfirm({ kind: 'chat' });
                }}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-rose-500/40 px-4 py-2 text-sm font-medium text-rose-500 hover:bg-rose-500/10"
              >
                <TrashIcon className="h-4 w-4" />
                Скрыть чат
              </button>
            )}
          </aside>

          {/* Messages */}
          <section className="overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm lg:order-1 lg:col-span-2">
            <div ref={scrollRef} className="h-[calc(100vh-15rem)] min-h-[360px] overflow-y-auto bg-bg-primary/40 px-4 py-4">
              {loading ? (
                <div className="space-y-4">
                  {[40, 60, 35, 55, 45].map((w, i) => (
                    <div key={i} className={`flex ${i % 2 ? 'justify-end' : 'justify-start'}`}>
                      <div className="h-12 animate-pulse rounded-2xl bg-bg-tertiary" style={{ width: `${w}%` }} />
                    </div>
                  ))}
                </div>
              ) : messages.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center text-text-muted">
                  <ChatBubbleOvalLeftEllipsisIcon className="mb-3 h-12 w-12" />
                  <p className="text-sm">В этом чате нет сообщений</p>
                </div>
              ) : (
                <ol className="space-y-1.5">
                  {messages.map((m, i) => {
                    const prev = messages[i - 1];
                    const newDay = !prev || new Date(prev.created_at).toDateString() !== new Date(m.created_at).toDateString();
                    const right = m.sender?.id != null && m.sender.id === user2.id;
                    const sameAuthorAsPrev = prev && !newDay && prev.sender?.id === m.sender?.id;
                    const removed = Boolean(m.deleted_at);
                    return (
                      <li key={m.id}>
                        {newDay && (
                          <div className="my-3 flex justify-center">
                            <span className="rounded-full bg-bg-tertiary px-3 py-0.5 text-[11px] font-medium text-text-secondary">
                              {formatDay(m.created_at)}
                            </span>
                          </div>
                        )}
                        <div className={`group flex items-end gap-2 ${right ? 'flex-row-reverse' : ''} ${sameAuthorAsPrev ? '' : 'mt-3'}`}>
                          <div className="w-7 shrink-0">
                            {!sameAuthorAsPrev && <Avatar user={right ? user2 : user1} className="h-7 w-7 text-[11px]" />}
                          </div>
                          <div
                            className={`max-w-[75%] overflow-hidden rounded-2xl px-3.5 py-2 text-sm ${
                              !removed && m.file_info && m.message_type === 'image' ? 'w-72' : ''
                            } ${
                              removed
                                ? 'border border-dashed border-border-primary bg-transparent text-text-muted'
                                : right
                                ? 'rounded-br-md bg-[#4F45E5] text-white'
                                : 'rounded-bl-md bg-bg-card text-text-primary ring-1 ring-border-primary'
                            }`}
                          >
                            {removed ? (
                              <span className="flex items-center gap-1.5 italic">
                                <EyeSlashIcon className="h-4 w-4" />
                                Скрыто{m.message ? `: ${m.message}` : ''}
                              </span>
                            ) : (
                              <MessageBody message={m} />
                            )}
                            <div className={`mt-1 flex items-center justify-end gap-1 text-[10px] ${right && !removed ? 'text-white/70' : 'text-text-muted'}`}>
                              {formatTime(m.created_at)}
                              {!removed && m.is_read && <span title="Прочитано">✓✓</span>}
                            </div>
                          </div>
                          {!removed && (
                            <button
                              type="button"
                              onClick={() => {
                                setActionError(null);
                                setConfirm({ kind: 'message', message: m });
                              }}
                              title="Скрыть сообщение"
                              aria-label="Скрыть сообщение"
                              className="mb-1 rounded-lg p-1.5 text-text-muted opacity-0 transition-opacity hover:bg-rose-500/10 hover:text-rose-500 focus:opacity-100 group-hover:opacity-100"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </section>
        </div>
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm.kind === 'chat' ? `Скрыть чат #${chat.id}?` : 'Скрыть сообщение?'}
          text={
            confirm.kind === 'chat'
              ? 'Переписка со всеми сообщениями будет скрыта у обоих пользователей. В админке чат останется в разделе «Скрытые».'
              : `«${(confirm.message.message || 'Вложение').slice(0, 120)}» — сообщение исчезнет у пользователей, в админке останется помеченным как скрытое.`
          }
          confirmLabel="Скрыть"
          busy={busy}
          error={actionError}
          onConfirm={runConfirmed}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
};

export default ChatDetails;
